import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Save, Trash2, FileText } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { organizationCache } from '@/utils/organizationCache';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';
import { useTheme } from '@/contexts/ThemeProvider';

const Notes = () => {
  const { t } = useTranslation();
  const { currentOrganization, switchOrganization, user } = useAuth();
  const { theme } = useTheme();
  const [notes, setNotes] = useState(currentOrganization?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const isMobile = useIsMobile();

  // Create BlockNote editor with image upload support
  const editor = useCreateBlockNote({
    uploadFile: async (file: File) => {
      if (!user || !currentOrganization) throw new Error('Not authenticated');
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('note-images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('note-images')
        .getPublicUrl(fileName);
      
      return data.publicUrl;
    },
  });

  // Load markdown into editor when organization notes change
  useEffect(() => {
    const loadContent = async () => {
      if (currentOrganization?.notes && editor) {
        try {
          const blocks = await editor.tryParseMarkdownToBlocks(currentOrganization.notes);
          editor.replaceBlocks(editor.document, blocks);
        } catch (error) {
          console.error('Error loading markdown:', error);
        }
      }
    };
    loadContent();
  }, [currentOrganization?.notes, editor]);

  const handleSave = async () => {
    if (!currentOrganization?.id || !editor) {
      toast.error(t('common.error'));
      return;
    }

    setIsSaving(true);
    try {
      // Convert editor content to markdown
      const markdown = await editor.blocksToMarkdownLossy(editor.document);
      
      const { error } = await supabase.rpc('update_organization_notes', {
        p_organization_id: currentOrganization.id,
        p_notes: markdown
      });

      if (error) throw error;

      setNotes(markdown);

      // Invalidate cached org and refresh data to ensure consistency across the app
      if (currentOrganization?.id) {
        organizationCache.invalidate(currentOrganization.id);
        await switchOrganization(currentOrganization.id);
      }

      toast.success(t('notes.saveSuccess'));
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error(t('notes.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (!currentOrganization?.id || !editor) {
      toast.error(t('common.error'));
      return;
    }

    setIsClearing(true);
    try {
      const { error } = await supabase.rpc('update_organization_notes', {
        p_organization_id: currentOrganization.id,
        p_notes: ''
      });

      if (error) throw error;

      setNotes('');
      editor.replaceBlocks(editor.document, []);
      
      // Invalidate cache and refresh organization data to ensure cleared notes persist after reload
      if (currentOrganization?.id) {
        organizationCache.invalidate(currentOrganization.id);
        await switchOrganization(currentOrganization.id);
      }
      
      toast.success(t('notes.clearSuccess'));
      setIsSheetOpen(false); // Close sheet on mobile
    } catch (error) {
      console.error('Error clearing notes:', error);
      toast.error(t('notes.clearError'));
    } finally {
      setIsClearing(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (!editor) return false;
    return JSON.stringify(editor.document) !== JSON.stringify(currentOrganization?.notes || '');
  }, [editor?.document, currentOrganization?.notes]);

  // Component to render delete confirmation (either dialog or sheet based on device)
  const DeleteConfirmation = () => {
    if (isMobile) {
      return (
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button
              variant="destructive"
              disabled={isClearing}
              className="gap-2 w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4" />
              {isClearing ? t('common.clearing') : t('notes.clearAll')}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[40vh]">
            <SheetHeader>
              <SheetTitle>{t('notes.confirmClearTitle')}</SheetTitle>
              <SheetDescription>
                {t('notes.confirmClearDescription')}
              </SheetDescription>
            </SheetHeader>
            <SheetFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setIsSheetOpen(false)}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleClear}
                disabled={isClearing}
                className="flex-1"
              >
                {isClearing ? t('common.clearing') : t('notes.clearAll')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="destructive"
            disabled={isClearing}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isClearing ? t('common.clearing') : t('notes.clearAll')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notes.confirmClearTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notes.confirmClearDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('notes.clearAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">{t('notes.title')}</h1>
          </div>
          <p className="text-muted-foreground">
            {t('notes.description')}
          </p>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="min-h-[60vh] md:min-h-[400px] border rounded-md overflow-hidden">
              <BlockNoteView 
                editor={editor} 
                theme={theme === 'dark' ? 'dark' : 'light'}
              />
            </div>
            
            {/* Mobile Layout - Stacked */}
            <div className="flex flex-col space-y-4 sm:hidden">
              <div className="flex flex-col space-y-3">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="gap-2 w-full"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? t('common.saving') : t('common.save')}
                </Button>
                
                {currentOrganization?.notes && <DeleteConfirmation />}
              </div>
            </div>

            {/* Desktop Layout - Horizontal */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? t('common.saving') : t('common.save')}
                </Button>
                
                {currentOrganization?.notes && <DeleteConfirmation />}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Notes;